import { ArrowRightOutlined, PlusOutlined } from '@ant-design/icons'
import { Button, Flex, Typography } from 'antd'
import { getCardPills } from 'features/libra/dashboard/ticketDashboard/filtering/cardIndex'
import type {
  CardView,
  PillCategories,
} from 'features/libra/dashboard/ticketDashboard/types'
import type { FC } from 'react'
import { memo, useMemo } from 'react'

import { LinkButton } from './LinkRow'
import { PillInfoList } from './PillInfoList'
import { PillList } from './PillList'

interface TicketDetailProps {
  card: CardView
  categories: PillCategories
  onCreateVersion?: (ticketId: string) => void
}

/** The card's headline ticket, with the card's own pills alongside its own. */
const RawTicketDetail: FC<TicketDetailProps> = ({
  card,
  categories,
  onCreateVersion,
}) => {
  const ticket = card.tickets[0]
  const pills = useMemo(() => getCardPills(card), [card])
  const summaryPills = useMemo(
    () => pills.filter((pill) => !categories[pill.category]?.showOnlyOnModal),
    [pills, categories]
  )
  const progressText =
    ticket.progress &&
    `${Math.round(ticket.progress.percent)}% complete${ticket.progress.inReview ? ' · in review' : ''}`
  const subtitle = [ticket.subtitle, progressText].filter(Boolean).join(' · ')

  return (
    <Flex vertical gap='small'>
      <Flex justify='space-between' align='center' gap='small'>
        <Flex vertical>
          <Typography.Text strong>{ticket.title}</Typography.Text>
          {subtitle && (
            <Typography.Text type='secondary'>{subtitle}</Typography.Text>
          )}
        </Flex>
        <Flex gap='small'>
          {ticket.canCreateVersion && onCreateVersion && (
            <Button
              icon={<PlusOutlined />}
              onClick={() => onCreateVersion(ticket.id)}>
              New Version
            </Button>
          )}
          {ticket.link && (
            <LinkButton
              type='primary'
              icon={<ArrowRightOutlined />}
              iconPlacement='end'
              to={ticket.link.to}
              search={ticket.link.search}>
              {ticket.link.label}
            </LinkButton>
          )}
        </Flex>
      </Flex>
      {summaryPills.length > 0 && (
        <PillList pills={summaryPills} categories={categories} />
      )}
      <PillInfoList pills={pills} categories={categories} />
    </Flex>
  )
}

export const TicketDetail = memo(RawTicketDetail)
